// src/services/api.ts — All backend API calls (fertilizer portal).
import axios from 'axios';
import type { Bag, Company, Dealer, FraudAlert, User } from '../types';

// Empty (relative) by default → API calls go to the same origin and the CRA
// dev-server proxy forwards them to the backend. This keeps everything on one
// origin, so an HTTPS tunnel (for phone GPS testing) has no mixed-content issue.
const BASE = process.env.REACT_APP_API_URL || '';

const api = axios.create({ baseURL: BASE });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // A 401 on the login call means "wrong password" — the page shows that itself.
    // Only an expired session on some other call should bounce to the login screen.
    const isLoginCall = String(err.config?.url || '').includes('/api/auth/login');
    if (err.response?.status === 401 && !isLoginCall) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Send fleet users back to the fleet login, not the fertilizer one.
      window.location.href = window.location.pathname.startsWith('/fleet') ? '/fleet/login' : '/login';
    }
    return Promise.reject(err);
  }
);

/** Standard `{ success, data }` envelope most fertilizer endpoints return. */
export interface Envelope<T> { success: boolean; data: T; total?: number; fromCache?: boolean }
/** Query-string params accepted by list endpoints. */
export type QueryParams = Record<string, string | number | boolean | undefined>;

// ── Auth ──────────────────────────────────────────────────
export interface LoginResponse { success: boolean; token: string; user: User }
export const login      = (email: string, password: string) => api.post<LoginResponse>('/api/auth/login', { email, password });
export const getMe      = () => api.get<{ success: boolean; user: User }>('/api/auth/me');
export const getUsers   = () => api.get<Envelope<User[]>>('/api/auth/users');
export const createUser = (data: Partial<User> & { password: string }) => api.post<Envelope<User>>('/api/auth/users', data);

// ── Dashboard (analytics shapes are declared by the page that renders them) ──
export const getDashboardStats  = <T = unknown>() => api.get<Envelope<T>>('/api/dashboard/stats');
export const getDashboardFunnel = <T = unknown>() => api.get<Envelope<T>>('/api/dashboard/funnel');
export const getByState         = <T = unknown>() => api.get<Envelope<T>>('/api/dashboard/by-state');
export const getRecentScans     = <T = unknown>(limit = 20) => api.get<Envelope<T>>(`/api/dashboard/recent-scans?limit=${limit}`);
export const getDailyTrend      = <T = unknown>() => api.get<Envelope<T>>('/api/dashboard/daily-trend');
export const getDealerBoard     = <T = unknown>() => api.get<Envelope<T>>('/api/dashboard/dealer-leaderboard');
export const getScanPointStats  = <T = unknown>() => api.get<Envelope<T>>('/api/dashboard/scan-point-stats');

// ── AI (Dashboard Ask AI) ─────────────────────────────────
export const askAI = <T = unknown>(question: string) => api.post<Envelope<T>>('/api/ai/ask', { question });

// ── Bags ──────────────────────────────────────────────────
export const getBags = (params?: QueryParams) =>
  api.get<Envelope<Bag[]> & { page?: number; limit?: number }>('/api/bags', { params });
export const getBag  = (bagId: string) => api.get<Envelope<Bag>>(`/api/bags/${bagId}`);

// ── Scan ──────────────────────────────────────────────────
export const getScanBag       = <T = unknown>(bagId: string) => api.get<Envelope<T> & { already_registered?: boolean; helpline?: string }>(`/api/scan/${bagId}`);
/** `_dev_otp` / `sms_mock` are only present while SMS runs in mock mode. */
export const sendOtp          = (mobile: string, bagId: string) =>
  api.post<{ success: boolean; message: string; _dev_otp?: string; sms_mock?: boolean }>('/api/scan/otp/send', { mobile, bagId });
/** Unlike most endpoints, farmer-status returns its fields at the top level (no `data`). */
export const getFarmerStatus  = <T = unknown>(mobile: string) => api.get<{ success: boolean } & T>('/api/scan/farmer-status', { params: { mobile } });
export const submitFarmerScan = <T = unknown>(data: Record<string, unknown>) => api.post<{ success: boolean; message?: string } & T>('/api/scan', data);
export const submitCheckpoint = <T = unknown>(data: Record<string, unknown>) => api.post<{ success: boolean } & T>('/api/scan/checkpoint', data);
export const getScanPointStatsToday = <T = unknown>() => api.get<Envelope<T>>('/api/scan/stats/by-point');
export const getBagJourney    = <T = unknown>(bagId: string) => api.get<Envelope<T>>(`/api/scan/journey/${bagId}`);

// ── Upload ────────────────────────────────────────────────
export const uploadExcel       = <T = unknown>(formData: FormData) =>
  api.post<Envelope<T>>('/api/upload/excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getUploadStatus   = <T = unknown>(id: string) => api.get<Envelope<T>>(`/api/upload/status/${id}`);
export const listUploads       = <T = unknown>() => api.get<Envelope<T>>('/api/upload/list');
export const getUploadTemplate = () => api.get<Blob>('/api/upload/template', { responseType: 'blob' });

// ── Alerts ────────────────────────────────────────────────
export const getAlerts    = (params?: QueryParams) => api.get<Envelope<FraudAlert[]>>('/api/alerts', { params });
export const resolveAlert = (id: string) => api.patch<Envelope<FraudAlert>>(`/api/alerts/${id}/resolve`);

// ── Dealers ───────────────────────────────────────────────
export const getDealers   = (params?: QueryParams) => api.get<Envelope<Dealer[]>>('/api/dealers', { params });
export const addDealer    = (data: Partial<Dealer>) => api.post<Envelope<Dealer>>('/api/dealers', data);
export const updateDealer = (id: string, data: Partial<Dealer>) => api.patch<Envelope<Dealer>>(`/api/dealers/${id}`, data);

// ── Farmers ───────────────────────────────────────────────
export const getFarmers = <T = unknown>(params?: QueryParams) => api.get<Envelope<T>>('/api/farmers', { params });

// ── Companies ─────────────────────────────────────────────
export const getCompanies = () => api.get<Envelope<Company[]>>('/api/companies');
export const addCompany   = (data: Partial<Company>) => api.post<Envelope<Company>>('/api/companies', data);

// ── Reports (CSV/Excel downloads) ─────────────────────────
export const getDeliveryReport = (params?: QueryParams) => api.get<Blob>('/api/reports/delivery', { params, responseType: 'blob' });
export const getFraudReport    = (params?: QueryParams) => api.get<Blob>('/api/reports/fraud',    { params, responseType: 'blob' });
export const getSubsidyReport  = (params?: QueryParams) => api.get<Blob>('/api/reports/subsidy',  { params, responseType: 'blob' });

export default api;
